import * as stylex from '@stylexjs/stylex';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { literal as vLiteral } from 'valibot';
import { object as vObject, string as vString } from 'valibot';

import {
  Button,
  EyeOffStrokeIcon,
  EyeStrokeIcon,
  Heading,
  Hint,
  Icon,
  IconButton,
  LabeledInput,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  TextField,
  TOAST_TIMEOUT,
  minDelay,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { userMutations } from '@/resources/user/user.api';
import { UserSchema } from '@/resources/user/user.schema';
import { tryCatch } from '@/utils/try-catch.util';

export const Route = createFileRoute('/_auth/reset-password')({
  component: RouteComponent,
  validateSearch: vObject({
    token: vString(),
  }),
});

function RouteComponent() {
  const id = useId();

  const { token } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { toastQueue } = Route.useRouteContext();

  const [isEnteredPasswordVisible, setIsEnteredPasswordVisible] = useState(false);
  const [isEnteredConfirmPasswordVisible, setIsEnteredConfirmPasswordVisible] = useState(false);

  const resetPasswordMutation = useMutation(userMutations.resetPassword);

  const form = useForm({
    defaultValues: {
      newPassword: '',
      confirmNewPassword: '',
    },
    onSubmit: async ({ value }) => {
      const { error } = await minDelay(tryCatch(resetPasswordMutation.mutateAsync({
        new_password: value.newPassword,
        token,
      })), PROGRESS_INDICATOR_MIN_DELAY_DURATION);

      if (!error) {
        navigate({ to: '/sign-in' });
        toastQueue.add({ message: 'Password updated!', variant: 'success' }, { timeout: TOAST_TIMEOUT });
      }
    },
  });

  return (
    <div {...stylex.props(styles.base)}>
      <form
        id={id}
        {...stylex.props(formStyles.base)}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}>
        <Heading level={1} size={28}>Reset password</Heading>
        <form.Field
          name="newPassword"
          validators={{ onMount: UserSchema.password, onChange: UserSchema.password }}>
          {(field) => {
            const shouldShowErrorHint = field.state.meta.isDirty && field.state.meta.errors.length > 0 && field.state.value.length > 0;

            return (
              <TextField
                autoComplete="off"
                autoFocus
                id={field.name}
                isInvalid={shouldShowErrorHint}
                name={field.name}
                type={isEnteredPasswordVisible ? 'text' : 'password'}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={field.handleChange}>
                <LabeledInput label="New password">
                  {(renderProps) =>
                    <>
                      {renderProps.defaultChildren}
                      <IconButton
                        aria-label="Show entered password"
                        excludeFromTabOrder
                        size={32}
                        variant="ghost"
                        onPress={() => setIsEnteredPasswordVisible(!isEnteredPasswordVisible)}>
                        {isEnteredPasswordVisible ? <EyeStrokeIcon /> : <EyeOffStrokeIcon />}
                      </IconButton>
                    </>}
                </LabeledInput>
                {shouldShowErrorHint &&
                  <Hint>
                    <Icon />
                    {field.state.meta.errors.map((error) => error?.message).join(', ')}
                  </Hint>}
              </TextField>
            );
          }}
        </form.Field>

        <form.Subscribe selector={(state) => state.values.newPassword}>
          {(newPassword) => !!newPassword &&
            <form.Field
              name="confirmNewPassword"
              validators={{ onMount: UserSchema.password, onChange: vLiteral(newPassword, 'Passwords must match') }}>
              {(field) => {
                const shouldShowErrorHint = field.state.meta.isDirty && field.state.meta.errors.length > 0 && field.state.value.length > 0;

                return (
                  <TextField
                    autoComplete="off"
                    id={field.name}
                    isInvalid={shouldShowErrorHint}
                    name={field.name}
                    type={isEnteredConfirmPasswordVisible ? 'text' : 'password'}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={field.handleChange}>
                    <LabeledInput label="Confirm password">
                      {(renderProps) =>
                        <>
                          {renderProps.defaultChildren}
                          <IconButton
                            aria-label="Show entered password"
                            excludeFromTabOrder
                            size={32}
                            variant="ghost"
                            onPress={() => setIsEnteredConfirmPasswordVisible(!isEnteredConfirmPasswordVisible)}>
                            {isEnteredConfirmPasswordVisible ? <EyeStrokeIcon /> : <EyeOffStrokeIcon />}
                          </IconButton>
                        </>}
                    </LabeledInput>
                    {shouldShowErrorHint &&
                      <Hint>
                        <Icon />
                        {field.state.meta.errors.map((error) => error?.message).join(', ')}
                      </Hint>}
                  </TextField>
                );
              }}
            </form.Field>}
        </form.Subscribe>

      </form>
      <form.Subscribe
        selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
        {([canSubmit, isSubmitting]) =>
          <Button
            form={id}
            isDisabled={!canSubmit}
            isPending={isSubmitting}
            isRounded
            size={48}
            type="submit">
            Save
          </Button>}
      </form.Subscribe>
    </div>
  );
}

const styles = stylex.create({
  base: {
    display: 'flex',
    flex: '1 0 0',
    flexDirection: 'column',
    gap: 20,
    maxWidth: 432,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: {
      default: 126,
      [breakpoints.large]: 176,
    },
    width: '100%',
  },
});

const formStyles = stylex.create({
  base: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
});
